import { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

export interface AgentCrmConnectionStatus {
  connected: boolean;
  connectedEmail?: string | null;
  connectedAtUtc?: string | null;
  reconnectRequired?: boolean;
}

const NOT_CONNECTED: AgentCrmConnectionStatus = { connected: false };

/**
 * Wraps the per-agent Dynamics 365 connect/disconnect/status endpoints
 * (server/Velaro.Admin/Controllers/DynamicsController.cs, "connect/*" actions).
 *
 * connect/start is an ordinary bearer-authenticated JSON call returning { launchUrl }
 * (the Admin API is bearer-only — a browser navigation can't carry the JWT, so a
 * redirect action there would 401). The browser then top-level-navigates to that
 * launchUrl (connect/launch, anonymous, 60-second signed ticket), which sets the
 * session-binding cookie and 302s to Microsoft. Same fetch-then-navigate shape as every
 * other OAuth connect flow in this app. status/disconnect are ordinary JSON calls.
 */
export default function useAgentCrmConnection(configId: number | null) {
  const api = useApi();
  const [status, setStatus] = useState<AgentCrmConnectionStatus>(NOT_CONNECTED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const refresh = useCallback(async () => {
    if (!configId) {
      setStatus(NOT_CONNECTED);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.get(
        `Dynamics/connect/status?configId=${configId}`
      );
      if (!res.ok) {
        setStatus(NOT_CONNECTED);
        setError(`Failed to load connection status (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      setStatus({
        connected: !!data.connected,
        connectedEmail: data.connectedEmail ?? null,
        connectedAtUtc: data.connectedAtUtc ?? null,
        reconnectRequired: !!data.reconnectRequired
      });
    } catch (err) {
      setStatus(NOT_CONNECTED);
      setError(
        `Failed to load connection status: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setLoading(false);
    }
  }, [api.admin, configId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Fetch the launch URL with the bearer JWT, then full top-level navigation. Never a
  // popup/postMessage: no token is ever visible to browser JS (see the plan's "Rejected
  // approach" section). Zero-arg: always targets the configId this hook was constructed
  // with, so caller and hook can't disagree on which config to act on.
  async function connect() {
    if (!configId || connecting) return;
    setConnecting(true);
    setError(null);
    try {
      // POST, never GET: api.admin.get() is a client-side cache (30 s fresh / 5 min SWR keyed on
      // the URL) and the launch ticket is single-use + 60 s — a cached one is dead on arrival.
      const res = await api.admin.post(`Dynamics/connect/start?configId=${configId}`);
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        setError(`Couldn't start the Dynamics 365 connect flow (HTTP ${res.status})${body ? `: ${body}` : ""}`);
        return;
      }
      const data = (await res.json()) as { launchUrl?: string };
      if (!data.launchUrl) {
        setError("Couldn't start the Dynamics 365 connect flow: no launch URL returned.");
        return;
      }
      window.location.href = data.launchUrl;
      // Intentionally leave `connecting` true here — the page is navigating away.
      return;
    } catch (err) {
      setError(
        `Couldn't start the Dynamics 365 connect flow: ${err instanceof Error ? err.message : String(err)}`
      );
    }
    setConnecting(false);
  }

  async function disconnect(): Promise<boolean> {
    if (!configId) return false;
    setDisconnecting(true);
    setError(null);
    try {
      const res = await api.admin.delete(`Dynamics/connect/${configId}`);
      if (!res.ok) {
        setError(`Failed to disconnect (HTTP ${res.status})`);
        return false;
      }
      await refresh();
      return true;
    } catch (err) {
      setError(
        `Failed to disconnect: ${err instanceof Error ? err.message : String(err)}`
      );
      return false;
    } finally {
      setDisconnecting(false);
    }
  }

  return { status, loading, error, connecting, disconnecting, connect, disconnect, refresh };
}
